import React from 'react'
import { Link } from 'react-router'
import { ChevronRight } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'

interface ShortcutProps {
  to: string
  title: string
  description: string
  meta?: string
}

function Shortcut({ to, title, description, meta }: ShortcutProps) {
  return (
    <Link
      to={to}
      className="group flex items-start justify-between gap-3 border border-rule rounded-sm px-3 py-3 hover:border-ink-300 hover:bg-paper-200/30 transition-colors"
    >
      <div className="min-w-0">
        <p className="text-base text-ink group-hover:underline transition-colors">
          {title}
        </p>
        <p className="text-sm text-ink-500 mt-0.5">{description}</p>
        {meta && (
          <p className="tabular-nums text-xs text-ink-400 mt-1.5">{meta}</p>
        )}
      </div>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-ink-400 opacity-0 transition-opacity duration-200 group-hover:opacity-100 mt-0.5"
        aria-hidden
      />
    </Link>
  )
}

const AccountOverviewPage: React.FC = () => {
  const { user } = useAuth()
  const { unreadCount } = useNotifications()

  if (!user) return null

  return (
    <div>
      <h1 className="text-2xl text-ink mb-1">Oversigt</h1>
      <p className="text-sm text-ink-500 mb-6">
        Genveje til dine lister, notifikationer og indstillinger.
      </p>

      <div className="space-y-2">
        <Shortcut
          to="/account/watchlist"
          title="Watchlist"
          description="Overvågede virksomheder og personer."
        />
        <Shortcut
          to="/account/notifikationer/indbakke"
          title="Notifikationer"
          description="Indbakke og notifikationsindstillinger."
          meta={
            unreadCount > 0
              ? `${unreadCount} ulæst${unreadCount === 1 ? '' : 'e'}`
              : undefined
          }
        />
        <Shortcut
          to="/account/indstillinger"
          title="Kontoindstillinger"
          description="Navn, e-mail og adgangskode."
        />
        {user.isAdmin && (
          <Shortcut
            to="/account/admin"
            title="Admin"
            description="Brugerstyring og invitationer."
          />
        )}
      </div>
    </div>
  )
}

export default AccountOverviewPage
